/* eslint-disable max-lines-per-function */
import { PatientReferenceIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';
import { ServiceLocationId } from '@dreryk/egabinet-healthcare-provider-published-language-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import {
   ContinuouslyUsedMedicineIcon as ContinuouslyUsedMedicineTabIcon,
   HeartIcon,
   NavTabs,
} from '@dreryk/react-components';

import { medicineSelectorTranslationKeys } from '../../translations';

import { MedicineSelectorTabLabel } from './MedicineSelector/MedicineSelectorTabLabel';
import { ContinuouslyUsedMedicineSelectorTable } from './MedicineSelector/MedicineSelectorTable/ContinuouslyUsedMedicineSelectorTable/ContinuouslyUsedMedicineSelectorTable';
import { FavoriteMedicinesSelectorTable } from './MedicineSelector/MedicineSelectorTable/FavoriteMedicinesSelectorTable/FavoriteMedicinesSelectorTable';
import { PatientPrescriptionHistoryPanel } from './PatientPrescriptionHistoryPanel/PatientPrescriptionHistoryPanel';
import {
   OnContinuouslyUsedMedicineSelection,
   OnMedicineSelection,
   PrescriptionPanelNavParams,
} from './PrescriptionIssuingPanel.types';
import { PrescriptionsHistoryDownload } from './prescriptionsHistoryDownload/PrescriptionsHistoryDownload';
import { prescriptionsHistoryDownloadTranslationKeys } from './prescriptionsHistoryDownload/translations';

type PrescriptionBrowseTabsProps = {
   readonly patientId: Patient['id'];
   readonly visitId: number;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly patientReferenceIdentifier: PatientReferenceIdentifier;
   readonly locationId: ServiceLocationId;
   readonly onMedicineSelection: OnMedicineSelection;
   readonly onContinuouslyUsedMedicineSelection: OnContinuouslyUsedMedicineSelection;
};

export const PrescriptionBrowseTabs = (props: PrescriptionBrowseTabsProps): JSX.Element => {
   const demo = isDemo();

   return (
      <NavTabs<PrescriptionPanelNavParams> testId={'prescriptionTabs'}>
         <NavTabs.Tab
            param={PrescriptionPanelNavParams.FAVORITE_MEDICINES}
            testId={'favoriteMedicines'}
            label={
               <MedicineSelectorTabLabel
                  label={medicineSelectorTranslationKeys.tabs.favoriteMedicines}
                  icon={<HeartIcon />}
                  testId={'favoriteMedicines'}
               />
            }
         >
            <FavoriteMedicinesSelectorTable
               patientId={props.patientId}
               onMedicineSelection={props.onMedicineSelection}
               visitDiagnoses={props.visitDiagnoses}
            />
         </NavTabs.Tab>

         <NavTabs.Tab
            param={PrescriptionPanelNavParams.PRESCRIPTION_HISTORY}
            testId={'prescriptionHistory'}
            label={
               <MedicineSelectorTabLabel
                  label={medicineSelectorTranslationKeys.tabs.prescriptionHistory}
                  testId={'prescriptionHistory'}
               />
            }
         >
            <PatientPrescriptionHistoryPanel
               patientId={props.patientId}
               visitId={props.visitId}
               visitDiagnoses={props.visitDiagnoses}
            />
         </NavTabs.Tab>

         {demo ? null : (
            <NavTabs.Tab
               param={PrescriptionPanelNavParams.DOWNLOAD_HISTORY}
               testId={'downloadHistory'}
               label={prescriptionsHistoryDownloadTranslationKeys.tabName}
            >
               <PrescriptionsHistoryDownload
                  patientId={props.patientId}
                  visitId={props.visitId}
                  patientReferenceIdentifier={props.patientReferenceIdentifier}
                  locationId={props.locationId}
               />
            </NavTabs.Tab>
         )}

         <NavTabs.Tab
            param={PrescriptionPanelNavParams.CONTINUOUSLY_USED_MEDICINES}
            testId={'continuouslyUsedMedicine'}
            label={
               <MedicineSelectorTabLabel
                  label={medicineSelectorTranslationKeys.tabs.continuouslyUsedMedicines}
                  icon={<ContinuouslyUsedMedicineTabIcon />}
                  testId={'continuouslyUsedMedicine'}
               />
            }
         >
            <ContinuouslyUsedMedicineSelectorTable
               patientId={props.patientId}
               onMedicineSelection={props.onMedicineSelection}
               onContinuouslyUsedMedicineSelection={props.onContinuouslyUsedMedicineSelection}
               visitDiagnoses={props.visitDiagnoses}
            />
         </NavTabs.Tab>
      </NavTabs>
   );
};
